import {
  IEmailRecipientFilterCondition,
  IEmailRecipientFilterGroup,
  EmailRecipientFilterComparison,
} from "../../interfaces/models/IEmailDistributionList";
import {
  ConditionOperator,
  normalizeGroupConnectors,
  Operator,
  RecipientCondition,
  RecipientConditionGroup,
  RecipientSelection,
} from "./types";

const FIELD_ID_TO_BACKEND_FIELD: Record<string, string> = {
  "person-id": "id",
  "practice-industry": "practiceIndustry",
  "practice-group": "practiceGroupId",
  "industry-group": "industryGroupId",
  "specialty-group": "specialtyGroupId",
  "private-group": "privateGroupId",
  members: "membershipStatus",
  "member-type": "memberType",
  contacts: "contactType",
  leadership: "leadership",
  "last-name": "lastName",
  "profile-content": "profileContent",
  "firm-company": "firm",
  jurisdiction: "jurisdiction",
  service: "serviceType",
  association: "industryOrganizationAffiliation",
  meeting: "eventId",
  "meeting-speaker": "meetingSpeakerEventId",
  "special-event": "eventId",
  region: "region",
  "mail-address": "firmEmail",
  "from-meeting-event": "eventId",
  email: "firmEmail",
};

const BACKEND_FIELD_TO_FIELD_ID: Record<string, string> = {
  id: "person-id",
  practiceIndustry: "practice-industry",
  practiceGroupId: "practice-group",
  industryGroupId: "industry-group",
  specialtyGroupId: "specialty-group",
  privateGroupId: "private-group",
  membershipStatus: "members",
  memberType: "member-type",
  contactType: "contacts",
  leadership: "leadership",
  lastName: "last-name",
  profileContent: "profile-content",
  firm: "firm-company",
  jurisdiction: "jurisdiction",
  serviceType: "service",
  industryOrganizationAffiliation: "association",
  eventId: "meeting",
  meetingSpeakerEventId: "meeting-speaker",
  region: "region",
  firmEmail: "mail-address",
};

const EXCLUDED_EMAIL_FIELD = "mail-address";
const EXCLUDED_EMAIL_BACKEND_FIELD = FIELD_ID_TO_BACKEND_FIELD[EXCLUDED_EMAIL_FIELD];

const normalizeScalarValue = (value: string | number): string =>
  String(value ?? "").trim();

const normalizeConditionValues = (
  value: string | number | string[] | number[],
): string[] => {
  const values = Array.isArray(value) ? value : [value];
  return values.map(normalizeScalarValue).filter(Boolean);
};

const toBackendField = (fieldId: string): string => {
  return FIELD_ID_TO_BACKEND_FIELD[fieldId] || fieldId;
};

const toUiField = (field: string): string => {
  return BACKEND_FIELD_TO_FIELD_ID[field] || field;
};

const toBackendComparison = (
  conditionOperator: ConditionOperator,
  valuesCount: number,
): EmailRecipientFilterComparison => {
  if (conditionOperator === "is-not") {
    return valuesCount > 1 ? "NOT_IN" : "NOT_EQUAL";
  }
  if (conditionOperator === "like") {
    return "CONTAINS";
  }
  if (conditionOperator === "is") {
    return "EQUAL";
  }
  if (valuesCount > 1) {
    return "IN";
  }
  return "EQUAL";
};

const mapFilterComparisonToConditionOperator = (
  comparison: string,
): ConditionOperator => {
  if (comparison === "NOT_EQUAL") {
    return "is-not";
  }
  if (comparison === "NOT_IN") {
    return "is-not";
  }
  if (comparison === "CONTAINS" || comparison === "STARTS_WITH") {
    return "like";
  }
  if (comparison === "ARRAY_CONTAINS") {
    return "is";
  }
  return "is-one-of";
};

const mapFilterConditionToRecipientCondition = (
  condition: IEmailRecipientFilterCondition,
  id: string,
  conditionOperatorOverride?: ConditionOperator,
): RecipientCondition => {
  const value = normalizeConditionValues(condition.value).join("|");

  return {
    id,
    fieldId: toUiField(condition.field),
    conditionOperator:
      conditionOperatorOverride ||
      mapFilterComparisonToConditionOperator(condition.comparison),
    valueId: value,
  };
};

const mapRecipientConditionToFilterNode = (
  condition: RecipientCondition,
): IEmailRecipientFilterCondition | IEmailRecipientFilterGroup | null => {
  const rawField = condition.fieldId?.trim();
  const rawValue = condition.valueId?.trim();
  if (!rawField || !rawValue) {
    return null;
  }

  const values = rawValue
    .split("|")
    .map((value) => value.trim())
    .filter(Boolean);
  if (!values.length) {
    return null;
  }

  const backendField = toBackendField(rawField);
  if (condition.conditionOperator === "is" && values.length > 1) {
    return {
      logic: "AND",
      conditions: values.map((value) => ({
        field: backendField,
        comparison: "EQUAL",
        value,
      })),
    };
  }

  const comparison = toBackendComparison(condition.conditionOperator, values.length);
  const value =
    comparison === "IN" || comparison === "NOT_IN" ? values : values[0];

  return {
    field: backendField,
    comparison,
    value,
  };
};

const buildFilterGroupFromConditions = (
  logic: Operator,
  conditions: RecipientCondition[],
): IEmailRecipientFilterGroup | null => {
  const parsedNodes = conditions
    .map(mapRecipientConditionToFilterNode)
    .filter(
      (
        node,
      ): node is IEmailRecipientFilterCondition | IEmailRecipientFilterGroup =>
        node !== null,
    );
  if (!parsedNodes.length) {
    return null;
  }

  const parsedConditions: IEmailRecipientFilterCondition[] = [];
  const parsedGroups: IEmailRecipientFilterGroup[] = [];

  for (const node of parsedNodes) {
    if ("logic" in node) {
      parsedGroups.push(node);
      continue;
    }
    parsedConditions.push(node);
  }

  return {
    logic,
    ...(parsedConditions.length > 0 ? { conditions: parsedConditions } : {}),
    ...(parsedGroups.length > 0 ? { groups: parsedGroups } : {}),
  };
};

const mapGroupedAllValuesCondition = (
  group: IEmailRecipientFilterGroup,
  id: string,
): RecipientCondition | null => {
  if (group.logic !== "AND") {
    return null;
  }
  if (group.groups && group.groups.length > 0) {
    return null;
  }
  if (!group.conditions || group.conditions.length < 2) {
    return null;
  }

  const [firstCondition] = group.conditions;
  if (!firstCondition) {
    return null;
  }

  const baseField = firstCondition.field;
  const values: string[] = [];

  for (const condition of group.conditions) {
    if (condition.field !== baseField) {
      return null;
    }
    if (condition.comparison !== "EQUAL") {
      return null;
    }

    const normalizedValues = normalizeConditionValues(condition.value);
    if (normalizedValues.length !== 1) {
      return null;
    }
    values.push(normalizedValues[0]);
  }

  return {
    id,
    fieldId: toUiField(baseField),
    conditionOperator: "is",
    valueId: values.join("|"),
  };
};

const normalizeExcludedEmails = (emails: string[]): string[] => {
  const uniqueEmails = new Set<string>();

  emails.forEach((email) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }
    uniqueEmails.add(normalizedEmail);
  });

  return Array.from(uniqueEmails);
};

const createEmptySelection = (): RecipientSelection => ({
  logic: "AND",
  groups: [],
  groupConnectors: [],
});

/** Build nested filter: OR segments of AND-combined groups (AND binds tighter than OR). */
const buildFilterFromGroupsAndConnectors = (
  builtGroups: IEmailRecipientFilterGroup[],
  connectors: Operator[],
): IEmailRecipientFilterGroup => {
  if (builtGroups.length === 1) {
    return builtGroups[0];
  }

  const segments: IEmailRecipientFilterGroup[][] = [];
  let currentSegment: IEmailRecipientFilterGroup[] = [builtGroups[0]];

  for (let index = 0; index < connectors.length; index += 1) {
    const connector = connectors[index];
    const nextGroup = builtGroups[index + 1];
    if (!nextGroup) {
      continue;
    }

    if (connector === "OR") {
      segments.push(currentSegment);
      currentSegment = [nextGroup];
      continue;
    }

    currentSegment.push(nextGroup);
  }
  segments.push(currentSegment);

  const orParts = segments.map((segment) => {
    if (segment.length === 1) {
      return segment[0];
    }
    return {
      logic: "AND" as const,
      groups: segment,
    };
  });

  if (orParts.length === 1) {
    return orParts[0];
  }

  return {
    logic: "OR",
    groups: orParts,
  };
};

const tryExtractGroupsAndConnectorsFromFilter = (
  filter: IEmailRecipientFilterGroup,
  nextId: () => string,
): { groups: RecipientConditionGroup[]; groupConnectors: Operator[] } | null => {
  const mapLeafGroup = (
    group: IEmailRecipientFilterGroup,
  ): RecipientConditionGroup | null =>
    mapFilterGroupToRecipientConditionGroup(group, nextId);

  if (
    filter.logic === "OR" &&
    filter.groups &&
    filter.groups.length > 0 &&
    (!filter.conditions || filter.conditions.length === 0)
  ) {
    const flatGroups: RecipientConditionGroup[] = [];
    const groupConnectors: Operator[] = [];

    for (const segment of filter.groups) {
      if (
        segment.logic === "AND" &&
        segment.groups &&
        segment.groups.length > 0 &&
        (!segment.conditions || segment.conditions.length === 0)
      ) {
        for (let index = 0; index < segment.groups.length; index += 1) {
          const mappedGroup = mapLeafGroup(segment.groups[index]);
          if (!mappedGroup) {
            continue;
          }

          if (flatGroups.length > 0) {
            groupConnectors.push(index === 0 ? "OR" : "AND");
          }
          flatGroups.push(mappedGroup);
        }
        continue;
      }

      const mappedGroup = mapLeafGroup(segment);
      if (!mappedGroup) {
        continue;
      }

      if (flatGroups.length > 0) {
        groupConnectors.push("OR");
      }
      flatGroups.push(mappedGroup);
    }

    if (flatGroups.length > 1) {
      return { groups: flatGroups, groupConnectors };
    }
  }

  if (
    filter.groups &&
    filter.groups.length > 1 &&
    (!filter.conditions || filter.conditions.length === 0)
  ) {
    const mappedGroups = filter.groups
      .map(mapLeafGroup)
      .filter((group): group is RecipientConditionGroup => group !== null);

    if (mappedGroups.length > 1) {
      const logic = filter.logic === "OR" ? "OR" : "AND";
      return {
        groups: mappedGroups,
        groupConnectors: Array.from(
          { length: mappedGroups.length - 1 },
          () => logic,
        ),
      };
    }
  }

  return null;
};

const mapFilterGroupToRecipientConditionGroup = (
  group: IEmailRecipientFilterGroup,
  nextId: () => string,
): RecipientConditionGroup | null => {
  const conditions: RecipientCondition[] = [];

  for (const condition of group.conditions || []) {
    conditions.push(
      mapFilterConditionToRecipientCondition(condition, nextId()),
    );
  }

  for (const nestedGroup of group.groups || []) {
    const groupedCondition = mapGroupedAllValuesCondition(nestedGroup, nextId());
    if (!groupedCondition) {
      continue;
    }
    conditions.push(groupedCondition);
  }

  if (!conditions.length) {
    return null;
  }

  return {
    id: `group-${nextId()}`,
    logic: group.logic === "OR" ? "OR" : "AND",
    conditions,
  };
};

const serializeFilterConditionForComparison = (
  condition: IEmailRecipientFilterCondition,
): string | null => {
  const field = condition.field?.trim();
  if (!field) {
    return null;
  }

  const rawValues = normalizeConditionValues(condition.value);
  const normalizedValue =
    condition.comparison === "BETWEEN" || condition.comparison === "BETWEEN_TS"
      ? rawValues.join("|")
      : rawValues.sort((left, right) => left.localeCompare(right)).join("|");

  return `${field}::${condition.comparison}::${normalizedValue}`;
};

export const serializeRecipientFilterForComparison = (
  group?: IEmailRecipientFilterGroup | null,
): string | null => {
  if (!group) {
    return null;
  }

  const parts = [
    ...(group.conditions || [])
      .map(serializeFilterConditionForComparison)
      .filter((item): item is string => !!item),
    ...(group.groups || [])
      .map(serializeRecipientFilterForComparison)
      .filter((item): item is string => !!item),
  ].sort((left, right) => left.localeCompare(right));

  if (!parts.length) {
    return null;
  }

  return `${group.logic}::${parts.join("||")}`;
};

export const buildRecipientFilterFromSelection = (
  selection: RecipientSelection,
  excludedEmails: string[] = [],
): IEmailRecipientFilterGroup | null => {
  const builtGroups = selection.groups
    .map((group) => buildFilterGroupFromConditions(group.logic, group.conditions))
    .filter((group): group is IEmailRecipientFilterGroup => group !== null);

  if (!builtGroups.length) {
    return null;
  }

  const connectors = normalizeGroupConnectors(
    selection.groups,
    selection.logic,
    selection.groupConnectors,
  );

  const baseFilter = buildFilterFromGroupsAndConnectors(builtGroups, connectors);

  const normalizedExcludedEmails = normalizeExcludedEmails(excludedEmails);
  if (!normalizedExcludedEmails.length) {
    return baseFilter;
  }

  return {
    logic: "AND",
    conditions: normalizedExcludedEmails.map((email) => ({
      field: EXCLUDED_EMAIL_BACKEND_FIELD,
      comparison: "NOT_EQUAL" as const,
      value: email,
    })),
    groups: [baseFilter],
  };
};

export const areRecipientSelectionsEqual = (
  left: RecipientSelection,
  right: RecipientSelection,
): boolean => {
  const leftFilter = buildRecipientFilterFromSelection(left);
  const rightFilter = buildRecipientFilterFromSelection(right);

  return (
    serializeRecipientFilterForComparison(leftFilter) ===
    serializeRecipientFilterForComparison(rightFilter)
  );
};

export const parseRecipientFilterForEditor = (
  filter?: IEmailRecipientFilterGroup | null,
): {
  selection: RecipientSelection;
  excludedEmails: string[];
} => {
  if (!filter) {
    return {
      selection: createEmptySelection(),
      excludedEmails: [],
    };
  }

  const rootConditions = filter.conditions || [];
  const retainedRootConditions: IEmailRecipientFilterCondition[] = [];
  const excludedEmails: string[] = [];

  if (filter.logic === "AND") {
    for (const condition of rootConditions) {
      const isExcludedEmailCondition =
        condition.field === EXCLUDED_EMAIL_BACKEND_FIELD &&
        (condition.comparison === "NOT_EQUAL" ||
          condition.comparison === "NOT_IN");

      if (!isExcludedEmailCondition) {
        retainedRootConditions.push(condition);
        continue;
      }

      excludedEmails.push(...normalizeConditionValues(condition.value));
    }
  } else {
    retainedRootConditions.push(...rootConditions);
  }

  const rootGroupCandidates = filter.groups || [];
  const normalizedRootGroup =
    filter.logic === "AND" &&
    excludedEmails.length > 0 &&
    retainedRootConditions.length === 0 &&
    rootGroupCandidates.length === 1
      ? rootGroupCandidates[0]
      : {
          logic: filter.logic,
          ...(retainedRootConditions.length > 0
            ? { conditions: retainedRootConditions }
            : {}),
          ...(rootGroupCandidates.length > 0 ? { groups: rootGroupCandidates } : {}),
        };

  const selection: RecipientSelection = {
    logic: normalizedRootGroup.logic === "OR" ? "OR" : "AND",
    groups: [],
    groupConnectors: [],
  };

  let nextIdValue = 1;
  const nextId = (): string => String(nextIdValue++);

  const extractedGroupsAndConnectors = tryExtractGroupsAndConnectorsFromFilter(
    normalizedRootGroup,
    nextId,
  );
  if (extractedGroupsAndConnectors) {
    selection.groups = extractedGroupsAndConnectors.groups;
    selection.groupConnectors = extractedGroupsAndConnectors.groupConnectors;
    selection.logic = selection.groupConnectors.includes("OR") ? "OR" : "AND";
    return {
      selection,
      excludedEmails: normalizeExcludedEmails(excludedEmails),
    };
  }

  const rootConditionsForEditor: RecipientCondition[] = [];
  for (const condition of normalizedRootGroup.conditions || []) {
    rootConditionsForEditor.push(
      mapFilterConditionToRecipientCondition(condition, nextId()),
    );
  }

  for (const nestedGroup of normalizedRootGroup.groups || []) {
    const groupedCondition = mapGroupedAllValuesCondition(nestedGroup, nextId());
    if (groupedCondition) {
      rootConditionsForEditor.push(groupedCondition);
      continue;
    }

    const mappedGroup = mapFilterGroupToRecipientConditionGroup(
      nestedGroup,
      nextId,
    );
    if (mappedGroup) {
      selection.groups.push(mappedGroup);
    }
  }

  if (rootConditionsForEditor.length > 0) {
    selection.groups.unshift({
      id: `group-${nextId()}`,
      logic: normalizedRootGroup.logic === "OR" ? "OR" : "AND",
      conditions: rootConditionsForEditor,
    });
  }

  selection.groupConnectors = normalizeGroupConnectors(
    selection.groups,
    selection.logic,
    selection.groupConnectors,
  );

  return {
    selection,
    excludedEmails: normalizeExcludedEmails(excludedEmails),
  };
};

export const cloneRecipientSelection = (
  selection: RecipientSelection,
): RecipientSelection => ({
  logic: selection.logic,
  groups: selection.groups.map((group) => ({
    ...group,
    conditions: group.conditions.map((condition) => ({ ...condition })),
  })),
  groupConnectors: normalizeGroupConnectors(
    selection.groups,
    selection.logic,
    selection.groupConnectors,
  ),
});

const areExcludedEmailListsEqual = (left: string[], right: string[]): boolean => {
  const normalizedLeft = normalizeExcludedEmails(left).sort((a, b) =>
    a.localeCompare(b),
  );
  const normalizedRight = normalizeExcludedEmails(right).sort((a, b) =>
    a.localeCompare(b),
  );

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

/** True when the step-2 editor content differs from a stored list filter. */
export const hasRecipientListFilterEdits = (
  selection: RecipientSelection,
  excludedEmails: string[],
  storedFilter?: IEmailRecipientFilterGroup | null,
): boolean => {
  const original = parseRecipientFilterForEditor(storedFilter);

  return (
    !areRecipientSelectionsEqual(selection, original.selection) ||
    !areExcludedEmailListsEqual(excludedEmails, original.excludedEmails)
  );
};
